/**
 * LEAVING A RETIREMENT VILLAGE FOR CARE, in the client's words (owner, 7 Oct 2026). The village pays
 * the exit entitlement months after the resident leaves, on the state's timeline; until then the
 * interest still counts as their home and the part of the room it will pay is charged daily. One
 * module for the words, so step 1, the funding card, the plan summary, the cards, the recap and the
 * advice document all say the same thing.
 */
import { formatCurrency } from '../../utils/index.ts';
import { phaseFromLabel } from './carePhases.ts';

/** A daily figure keeps its cents: $69.95 a day is a real quote, and "$70" would overstate the wait. */
const perDay = (n: number): string => `$${(Math.round(n * 100) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface VillageFacts {
    name: string;
    entryContribution: number;
    resaleValue: number;
    yearsResidency: number;
    departureFee: number;
    reinstatement: number;
    residentShareOfGain: number;
    entitlement: number;
    refundMonths: number;
    /** YYYY-MM-DD */
    refundDate: string;
    state: string;
}

export interface VillageInterim {
    days: number;
    perDay: number;
    total: number;
}

export interface VillageFeesAfterLeavingFacts {
    total: number;
    fullDays: number;
    sharedDays: number;
    source?: string;
}

/**
 * THE WAIT IS AN ASSUMPTION, SAID SO (owner, 8 Oct 2026). Nobody knows how long a unit takes to
 * resell, so the plan prices the state's limit (or the months the adviser entered) and states the
 * range: nothing if it sells straight away, the full figure if it takes the whole period.
 */
export function villageWaitSensitivityLine(f: { refundMonths: number; interim?: VillageInterim | null; feesAfterLeaving?: VillageFeesAfterLeavingFacts | null }): string {
    const atLimit = (f.interim?.total || 0) + (f.feesAfterLeaving?.total || 0);
    if (!(atLimit > 0.5)) return '';
    return `How long the unit takes to resell is an assumption: if it sells straight away the wait costs nothing; if it takes the full ${f.refundMonths} months, about ${formatCurrency(Math.round(atLimit))}.`;
}

/** "The village's fees continue after leaving — 42 days (the NSW rule): about $1,700, taken from the payout." */
export function villageFeesAfterLeavingLine(f: VillageFeesAfterLeavingFacts): string {
    if (!(f.total > 0.5)) return '';
    const period = f.sharedDays > 0
        ? `${f.fullDays} days in full, then a share for ${f.sharedDays} more`
        : `${f.fullDays} days`;
    return `The village's fees continue after leaving — ${period}${f.source ? ` (${f.source})` : ''}: about ${formatCurrency(Math.round(f.total))}, taken from the payout.`;
}

export function describeResidency(years: number): string {
    if (years < 1) return 'under a year';
    // To the nearest half-year: "6½ years" for 6.497.
    const rounded = Math.round(years * 2) / 2;
    const whole = Math.floor(rounded);
    const half = rounded - whole >= 0.5;
    return `${whole}${half ? '½' : ''} ${whole === 1 && !half ? 'year' : 'years'}`;
}

/** "by January 2027" */
export function refundByLabel(f: Pick<VillageFacts, 'refundDate'>): string {
    return `by ${phaseFromLabel(f.refundDate)}`;
}

/** Step 1's summary of the village the person is in now, and what leaving it pays. */
export function villageSummary(f: VillageFacts): string {
    const stayed = f.yearsResidency > 0 ? `has lived in the village for ${describeResidency(f.yearsResidency)}` : 'lives in a retirement village';
    const fee = f.departureFee > 0.5 ? `the departure fee of ${formatCurrency(Math.round(f.departureFee))}` : 'no departure fee';
    const reinstate = f.reinstatement > 0.5 ? ` and ${formatCurrency(Math.round(f.reinstatement))} to reinstate the unit` : '';
    const gain = f.residentShareOfGain > 0.5 ? `, and keeping ${formatCurrency(Math.round(f.residentShareOfGain))} of the gain on resale` : '';
    const resale = f.resaleValue > 0 && Math.abs(f.resaleValue - f.entryContribution) > 0.5 ? ` and the unit is expected to sell for ${formatCurrency(f.resaleValue)}` : '';
    return `${f.name} ${stayed}; the entry contribution was ${formatCurrency(f.entryContribution)}${resale}. On leaving for care the unit is sold back to the village: after ${fee}${reinstate}${gain}, about ${formatCurrency(Math.round(f.entitlement))} comes back. The village has up to ${f.refundMonths} months to pay it (${refundByLabel(f)}). Until then it still counts as ${f.name}'s home; from then it is money in the bank.`;
}

/** The funding card's line: what the village money does for the room, and what the wait costs. */
export function villageRefundLine(f: VillageFacts, fromVillage: number, interim: VillageInterim, useForRoom: boolean, atMove = false): string {
    if (atMove) {
        return !useForRoom || fromVillage <= 0.5
            ? `The village's ${formatCurrency(Math.round(f.entitlement))} exit payment is paid at the move and goes to the bank; the room is paid from savings and the strategies above.`
            : `${formatCurrency(Math.round(fromVillage))} of the room is paid from the village's exit payment at the move. Savings only need to cover what it does not.${f.entitlement - fromVillage > 0.5 ? ` The other ${formatCurrency(Math.round(f.entitlement - fromVillage))} goes to the bank.` : ''}`;
    }
    if (!useForRoom || fromVillage <= 0.5) {
        return `The village's ${formatCurrency(Math.round(f.entitlement))} exit payment goes to the bank when it arrives (${refundByLabel(f)}); the room is paid from savings and the strategies above.`;
    }
    const wait = interim.total > 0.5 ? ` Until then the home charges ${perDay(interim.perDay)} a day on that part of the room — about ${formatCurrency(Math.round(interim.total))} over the ${interim.days} days.` : '';
    const rest = f.entitlement - fromVillage > 0.5 ? ` The other ${formatCurrency(Math.round(f.entitlement - fromVillage))} of the exit payment goes to the bank.` : '';
    return `${formatCurrency(Math.round(fromVillage))} of the room is paid from the village's exit payment when it arrives, ${refundByLabel(f)}.${wait} Savings only need to cover what the village money does not.${rest}`;
}

/** The collapsed funding card, where the village money covers the whole room. */
export function villageCoveredLine(f: VillageFacts, fromVillage: number, interim: VillageInterim, roomPrice: number, atMove = false): string {
    const wait = !atMove && interim.total > 0.5 ? ` Until it arrives (${refundByLabel(f)}) the home charges ${perDay(interim.perDay)} a day on the room — about ${formatCurrency(Math.round(interim.total))} in all.` : atMove ? ' It is paid at the move.' : '';
    return `Fully covered — the village's ${formatCurrency(Math.round(f.entitlement))} exit payment pays the whole ${formatCurrency(roomPrice)} room price, so nothing needs to come from savings.${wait}${f.entitlement - fromVillage > 0.5 ? ` The other ${formatCurrency(Math.round(f.entitlement - fromVillage))} goes to the bank.` : ''}`;
}

export interface VillagePieceFacts {
    name: string;
    /** "January 2027" — when the person leaves. */
    leaveLabel: string;
    entitlement: number;
    refundMonths: number;
    refundDate: string;
    roomPrice: number;
    /** The deposit the plan reaches once the village money is in: from savings at entry plus the village's part. */
    deposit: number;
    fromVillage: number;
    fromSavings: number;
    /** "$50,000 from anz and $44,000 from shares", or '' */
    soldPhrase: string;
    toBank: number;
    interim: VillageInterim | null;
    /** The old unit's fees after leaving, on the state's rule; null or zero where none. */
    feesAfterLeaving?: VillageFeesAfterLeavingFacts | null;
    /** The payout taken as paid at the move (owner, 8 Oct 2026): no wait, no fees after leaving. */
    paidAtMove?: boolean;
    dapPerDay: number;
}

/** The recap's and the advice document's paragraph on leaving the village. */
export function villagePiece(f: VillagePieceFacts): string {
    const unpaid = Math.max(0, f.roomPrice - f.deposit);
    const parts: string[] = [];
    if (f.fromSavings > 0.5) parts.push(`${formatCurrency(Math.round(f.fromSavings))} at entry${f.soldPhrase ? ` from savings (${f.soldPhrase})` : ' from savings'}`);
    if (f.fromVillage > 0.5) parts.push(`${formatCurrency(Math.round(f.fromVillage))} from the village money when it arrives`);
    const paidWith = f.deposit <= 0.5
        ? `nothing is paid up front for the ${formatCurrency(f.roomPrice)} room, so the whole of it is charged as a daily payment`
        : `${formatCurrency(Math.round(f.deposit))} of the ${formatCurrency(f.roomPrice)} room is paid as a deposit — ${parts.join(', and ')}${unpaid > 0.5 ? `; the unpaid ${formatCurrency(Math.round(unpaid))} is charged as a daily payment of ${perDay(f.dapPerDay)} a day` : ''}`;
    const wait = f.interim && f.interim.total > 0.5 ? ` Until the village pays, ${perDay(f.interim.perDay)} a day is charged on its ${formatCurrency(Math.round(f.fromVillage))} share of the room — about ${formatCurrency(Math.round(f.interim.total))} in all.` : '';
    const bank = f.toBank > 0.5 ? ` The other ${formatCurrency(Math.round(f.toBank))} of the exit payment goes to the bank.` : '';
    const fees = f.feesAfterLeaving && f.feesAfterLeaving.total > 0.5 ? ` ${villageFeesAfterLeavingLine(f.feesAfterLeaving)}` : '';
    const range = f.paidAtMove ? '' : villageWaitSensitivityLine(f);
    const sensitivity = range ? ` ${range}` : '';
    const pays = f.paidAtMove
        ? `the unit is sold back and the village pays about ${formatCurrency(Math.round(f.entitlement))} at the move`
        : `the unit is sold back and the village pays about ${formatCurrency(Math.round(f.entitlement))} ${refundByLabel(f)} (it has up to ${f.refundMonths} months)`;
    return `${f.name} leaves the village in ${f.leaveLabel}; ${pays}. ${paidWith}.${f.paidAtMove ? '' : wait}${bank}${f.paidAtMove ? '' : fees}${sensitivity} From the day the village pays, the entry contribution no longer counts as a home: ${f.name} is assessed as a non-homeowner, with the deposit exempt for the Age Pension.`;
}

/** The village and granny flat modules: what the current unit pays back when the client moves on (8 Oct 2026). */
export function villageMoveOutLine(f: { name: string; entitlement: number; refundDate: string; refundMonths: number; state: string; feesAfterLeaving?: VillageFeesAfterLeavingFacts | null }, destination: 'village' | 'granny_flat', opts: { atMove?: boolean } = {}): string {
    const to = destination === 'village' ? 'the new village' : 'the family';
    if (opts.atMove) {
        return `The current unit pays back about ${formatCurrency(Math.round(f.entitlement))}. Paid at the move, that money goes towards ${to} first and savings cover the rest. Its fees stop on the day of the move.`;
    }
    const limit = f.state ? `the ${f.state} limit` : 'the time limit assumed';
    const fees = f.feesAfterLeaving && f.feesAfterLeaving.total > 0.5
        ? ` ${villageFeesAfterLeavingLine(f.feesAfterLeaving)} ${villageWaitSensitivityLine({ refundMonths: f.refundMonths, feesAfterLeaving: f.feesAfterLeaving })}`
        : ' Its fees stop on the day of the move.';
    return `The current unit pays back about ${formatCurrency(Math.round(f.entitlement))}, within ${f.refundMonths} months of leaving (${limit}) — ${refundByLabel(f)}. Until then savings pay for ${to}; the payout tops them back up when it arrives.${fees}`;
}

/** The walk-in note: what the village money does with no plan. */
export function villageWalkInLine(f: Pick<VillageFacts, 'entitlement' | 'refundDate'>): string {
    return `; the village's ${formatCurrency(Math.round(f.entitlement))} exit payment goes to the bank when it arrives, ${refundByLabel(f)}`;
}
